// src/firebase.js
// Firebase configuration for PlanBoss (the Firebase project id stays
// "measure-up-nz": Google never lets a project id change)
// Note: apiKey is safe to expose in client code - it's a public identifier,
// not a secret. Real security comes from Firebase Auth rules and
// authorized domains (configured in Firebase Console).

import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyAoykyzL71XsSop7htUL10WLyzHc0zZzUs",
  authDomain: "measure-up-nz.firebaseapp.com",
  projectId: "measure-up-nz",
  storageBucket: "measure-up-nz.firebasestorage.app",
  messagingSenderId: "623462446263",
  appId: "1:623462446263:web:1fedc3c7d350dfa7ff8932",
  measurementId: "G-V1ZB6S3QPE",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// auth-continue-2026-10-21: Google has locked this project's email templates
// (EMAIL_TEMPLATE_UPDATE_NOT_ALLOWED), so the action URL can't be pointed at
// PlanBoss's own page yet and the links open Firebase's page at
// measure-up-nz.firebaseapp.com. A continue URL puts a "Continue" button on
// that page, back to the PlanBoss address the person was using (on-device
// jobs live per address while filing is off). If Firebase won't take the
// address (not on Authorized domains), the email still goes, without it.
function pbContinueSettings() {
  try {
    const o = window.location.origin;
    if (/^https:\/\/[^/]+$/.test(o)) return { url: o + "/" };
  } catch (e) {}
  return null;
}
export async function pbSendWithContinue(send) {
  const settings = pbContinueSettings();
  if (!settings) return send(undefined);
  try {
    return await send(settings);
  } catch (e) {
    const code = String((e && e.code) || "");
    if (/continue-uri|unauthorized-domain/.test(code)) return send(undefined);
    throw e;
  }
}

// Helper: signup with email verification
// Returns { user } on success, throws Error on failure with .code and .message
export async function fbSignup(email, password) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await pbSendWithContinue((s) => sendEmailVerification(cred.user, s));
  return { user: cred.user };
}

// Helper: login (does NOT block on unverified - caller decides what to do)
export async function fbLogin(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return { user: cred.user };
}

// Helper: send password reset email
export async function fbResetPassword(email) {
  await pbSendWithContinue((s) => sendPasswordResetEmail(auth, email, s));
}

// Helper: resend verification email to currently logged-in user
export async function fbResendVerification() {
  try { if (auth.authStateReady) await auth.authStateReady(); } catch (e) {}
  if (!auth.currentUser) {
    const e = new Error("You're signed out. Sign in again, then send the link.");
    e.code = "auth/no-current-user";
    throw e;
  }
  const u = auth.currentUser;
  await pbSendWithContinue((s) => sendEmailVerification(u, s));
}

// verify-admin-2026-10-20: once the user has clicked the verification link
// (in another tab or their mail app), this device's sign-in still says
// "unverified" until it asks Google again. This asks, then gets a fresh token
// so the server sees it too. Returns { emailVerified }, or null if signed out.
export async function fbRefreshSignIn() {
  try { if (auth.authStateReady) await auth.authStateReady(); } catch (e) {}
  const u = auth.currentUser;
  if (!u) return null;
  await u.reload();
  await u.getIdToken(true);
  return { emailVerified: !!(auth.currentUser && auth.currentUser.emailVerified) };
}

// auth-action-2026-10-21: the links in Firebase's emails (verify email, reset
// password) open PlanBoss's own page, www.planboss.co.nz/?mode=…&oobCode=…
// (Firebase console → Authentication → Templates → Customize action URL),
// not Firebase's measure-up-nz.firebaseapp.com one. These finish them.
export async function fbApplyActionCode(code) {
  await applyActionCode(auth, code);
}
// The email address a reset link is for (also catches a used or expired link).
export async function fbVerifyPasswordResetCode(code) {
  return verifyPasswordResetCode(auth, code);
}
export async function fbConfirmPasswordReset(code, newPassword) {
  await confirmPasswordReset(auth, code, newPassword);
}

// Helper: sign out
export async function fbSignOut() {
  await signOut(auth);
}

// security-v1-2026-10-12: the signed-in user's ID token, sent to the server
// with every scan so the server knows who is asking. Waits for Firebase to
// restore the session on page load. Returns null when nobody is signed in.
export async function fbIdToken() {
  try { if (auth.authStateReady) await auth.authStateReady(); } catch (e) {}
  const u = auth.currentUser;
  return u ? await u.getIdToken() : null;
}

// store-v1-2026-10-12: delete the signed-in user's sign-in from the device.
// Used when the server's filing system is off (the server has already
// cancelled the subscription); otherwise the server deletes the account, its
// jobs and the sign-in together. Firebase wants a recent sign-in for this
// (auth/requires-recent-login): fbReauthenticate just before takes care of it.
export async function fbDeleteUser() {
  if (!auth.currentUser) {
    const e = new Error("Not signed in");
    e.code = "auth/no-current-user";
    throw e;
  }
  await deleteUser(auth.currentUser);
}

// plancheck-v1-2026-10-13: the password again before deleting the account.
// Proves it's them, and makes the sign-in "recent" so deleteUser can't fail
// with auth/requires-recent-login half-way through a deletion.
export async function fbReauthenticate(password) {
  const u = auth.currentUser;
  if (!u || !u.email) {
    const e = new Error("Not signed in");
    e.code = "auth/no-current-user";
    throw e;
  }
  await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, password));
}

// Helper: subscribe to auth state changes
export { onAuthStateChanged };

// Helper: convert Firebase error codes to user-friendly messages
export function fbErrorMessage(err) {
  const code = err?.code || "";
  const messages = {
    "auth/email-already-in-use": "An account with this email already exists. Try logging in instead.",
    "auth/invalid-email": "That doesn't look like a valid email address.",
    "auth/weak-password": "Use a password of at least 8 characters.",
    "auth/password-does-not-meet-requirements": "Use a password of at least 8 characters.",
    "auth/user-not-found": "No account found with this email. Sign up instead?",
    "auth/wrong-password": "Incorrect password. Try again or use 'Forgot password?'",
    "auth/invalid-credential": "Email or password is incorrect.",
    "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
    "auth/network-request-failed": "Network error. Check your internet connection.",
    "auth/user-disabled": "This account has been disabled. Contact support.",
  };
  return messages[code] || err?.message || "Something went wrong. Please try again.";
}
